/**
 * SimpleEdge (Canvas V2)
 *
 * Single edge type: white Bezier curve, hidden by default.
 * Visibility controlled by connected-node state.
 *
 * Visibility rules (priority order):
 * 1. Running (source or target status === 'running'): 90% opacity
 * 2. Hovered/selected (source or target node hovered/selected, or edge selected): 90% opacity
 * 3. Default: 0% opacity (invisible)
 *
 * Dead code removed: LabeledEdge, TaperedEdge, FloatingEdgeUtils,
 * canvasDragState, connectionGenerator (per spec 03).
 */

import { memo, useMemo } from 'react'
import { EdgeProps, getBezierPath, useStore } from 'reactflow'
import { useHoveredNodeIdSelector } from './NodeHoverContext'
import { useRenderActiveEdgesOnly } from './CanvasEdgesContext'
import { isEdgeActiveNodeStatus, isEdgeRunningNodeStatus } from './canvasEdgeVisibility'

/** Read node execution status from the React Flow store. */
function selectNodeStatus(nodeId: string) {
  return (store: { nodeInternals: Map<string, { data?: { status?: string }; selected?: boolean }> }): string => {
    const n = store.nodeInternals.get(nodeId)
    if (!n) return 'idle'
    return n.data?.status ?? 'idle'
  }
}

/** Check if a node is selected. */
function selectNodeSelected(nodeId: string) {
  return (store: { nodeInternals: Map<string, { data?: { status?: string }; selected?: boolean }> }): boolean => {
    const n = store.nodeInternals.get(nodeId)
    return n?.selected ?? false
  }
}

export const SimpleEdge = memo(function SimpleEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  selected,
}: EdgeProps) {
  // Memoize selectors to prevent zustand re-subscriptions
  const sourceStatusSel = useMemo(() => selectNodeStatus(source), [source])
  const targetStatusSel = useMemo(() => selectNodeStatus(target), [target])
  const sourceSelectedSel = useMemo(() => selectNodeSelected(source), [source])
  const targetSelectedSel = useMemo(() => selectNodeSelected(target), [target])

  const sourceStatus = useStore(sourceStatusSel)
  const targetStatus = useStore(targetStatusSel)
  const sourceSelected = useStore(sourceSelectedSel)
  const targetSelected = useStore(targetSelectedSel)
  const renderActiveEdgesOnly = useRenderActiveEdgesOnly()
  const hoveredSel = useMemo(
    () => (hoveredId: string | null) =>
      renderActiveEdgesOnly && (hoveredId === source || hoveredId === target),
    [renderActiveEdgesOnly, source, target],
  )
  const isHovered = useHoveredNodeIdSelector(hoveredSel)

  const isConnectedToRunningNode = isEdgeRunningNodeStatus(sourceStatus) || isEdgeRunningNodeStatus(targetStatus)
  const isConnectedToActiveNode = isEdgeActiveNodeStatus(sourceStatus) || isEdgeActiveNodeStatus(targetStatus)
  const isActive = isConnectedToRunningNode || (renderActiveEdgesOnly && isConnectedToActiveNode)
  const isNodeSelected = sourceSelected || targetSelected
  const isEdgeSelected = selected === true

  const [edgePath] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  })

  // Determine opacity (priority: running/selected > default)
  let opacity = 0
  if (isActive || isNodeSelected || isEdgeSelected || (renderActiveEdgesOnly && isHovered)) {
    opacity = 0.9
  }

  return (
    <g style={{ pointerEvents: 'none' }}>
      {/* Invisible path kept for geometry consistency, but it must not capture canvas drags. */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        className="react-flow__edge-interaction"
        style={{ pointerEvents: 'none' }}
      />
      {/* Visible white Bezier edge */}
      <path
        id={id}
        d={edgePath}
        fill="none"
        stroke="#ffffff"
        strokeWidth={1.5}
        opacity={opacity}
        className="react-flow__edge-path"
        style={{
          pointerEvents: 'none',
          transition: 'opacity 0.2s ease-in-out',
        }}
      />
    </g>
  )
})

/**
 * Edge types registry for React Flow.
 * Only SimpleEdge exists (no LabeledEdge, no TaperedEdge).
 */
export const edgeTypes = {
  default: SimpleEdge,
}
